import { ReactNode } from "react";
import { FaUsersCog, FaHammer, FaPen, FaEnvelope, FaUser, FaClock, FaDatabase, FaLink, FaCar, FaWpforms, FaUsers, FaBell, FaWrench, FaBullhorn, FaClipboardList, FaCogs, FaPiggyBank } from "react-icons/fa";
import { canViewArea } from "../../helpers/areaAccess";

export const ADMINISTRATOR_ROLE = "Portal.Administrator";

export interface ManagementOptionDef {
  title: string;
  subTitle: string;
  to: string;
  icon: ReactNode;
  /**
   * The role area that opens this option, e.g. "Users". Which of its roles
   * count is set in helpers/areaAccess.ts.
   */
  area?: string;
  /**
   * Exact roles that open this option, for screens with a single role of their
   * own (e.g. "ServiceTypes.Manage"). With neither, administrators only.
   */
  roles?: string[];
}

/** The options on the Management page, in the order they are shown. */
export const MANAGEMENT_OPTIONS: ManagementOptionDef[] = [
  { title: "Announcements", subTitle: "Banners shown at the top of every page", to: "/management/announcements", icon: <FaBullhorn />, roles: ["Announcements.Manage"] },
  { title: "Accounts", subTitle: "Manage accounts", to: "/accounts", icon: <FaUser />, area: "Accounts" },
  { title: "Database Explorer", subTitle: "Explore database tables and export data", to: "/database-explorer", icon: <FaDatabase />, area: "DatabaseExplorer" },
  { title: "Forms", subTitle: "Manage forms and form configurations", to: "/management/forms", icon: <FaWpforms />, area: "Forms" },
  { title: "Groups", subTitle: "Manage user groups and permissions", to: "/management/groups", icon: <FaUsers />, area: "Groups" },
  { title: "Invoice Email Templates", subTitle: "The subjects and messages invoices are emailed with", to: "/management/invoice-email", icon: <FaEnvelope />, roles: ["InvoiceEmailTemplates.Manage"] },
  { title: "Invoice Descriptions", subTitle: "Manage invoice descriptions", to: "/invoice-descriptions", icon: <FaPen />, area: "InvoiceDescriptions" },
  { title: "Links", subTitle: "Manage system links", to: "/management/links", icon: <FaLink />, area: "Links" },
  { title: "Notifiable Works", subTitle: "Notifiable works on jobs and the documents they need", to: "/management/notifiable-works", icon: <FaClipboardList />, roles: ["NotifiableWorks.Manage"] },
  { title: "Notifications", subTitle: "Send and manage system notifications", to: "/management/notifications", icon: <FaBell />, area: "Notifications" },
  { title: "Piggy Bank", subTitle: "Everyone's piggy bank, and the catalogue of items to give", to: "/management/piggy-bank", icon: <FaPiggyBank />, roles: ["PiggyBank.Manage"] },
  { title: "Service Types", subTitle: "Service types on service reminders", to: "/management/service-types", icon: <FaCogs />, roles: ["ServiceTypes.Manage"] },
  { title: "Text Messages", subTitle: "Manage text message content", to: "/text-messages", icon: <FaEnvelope />, area: "TextMessages" },
  { title: "Timesheets", subTitle: "Manage employee's timesheets", to: "/timesheets", icon: <FaClock />, area: "Timesheets" },
  { title: "Tools", subTitle: "Manage company tools", to: "/tools", icon: <FaWrench />, area: "Tools" },
  { title: "Users", subTitle: "Manage user accounts and permissions", to: "/users", icon: <FaUsersCog />, area: "Users" },
  { title: "Vehicles", subTitle: "Manage company vehicles", to: "/vehicles", icon: <FaCar />, area: "Vehicles" },
  { title: "Worktypes", subTitle: "Manage, add and remove worktypes", to: "/worktypes", icon: <FaHammer />, area: "WorkTypes" },
];

const asList = (allowedRoutes: unknown): string[] =>
  Array.isArray(allowedRoutes) ? allowedRoutes : [];

/** Administrators see every option; anyone else needs one of its roles. */
export const canOpenManagementOption = (allowedRoutes: unknown, option: ManagementOptionDef): boolean => {
  const allowed = asList(allowedRoutes);

  if (allowed.includes(ADMINISTRATOR_ROLE)) {
    return true;
  }

  return (option.roles ?? []).some((role) => allowed.includes(role))
    || (option.area !== undefined && canViewArea(allowed, option.area));
};

/**
 * Whether the page an option links to may be opened. Shares the option's rule
 * so a tile never leads to "no permissions". Unknown paths are admin only.
 */
export const canOpenManagementPath = (allowedRoutes: unknown, to: string): boolean => {
  const option = MANAGEMENT_OPTIONS.find((o) => o.to === to);

  return option
    ? canOpenManagementOption(allowedRoutes, option)
    : asList(allowedRoutes).includes(ADMINISTRATOR_ROLE);
};

export const visibleManagementOptions = (allowedRoutes: unknown): ManagementOptionDef[] =>
  MANAGEMENT_OPTIONS.filter((option) => canOpenManagementOption(allowedRoutes, option));
